import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const QRScanner = ({ onScanResult, isScanning, onToggleScanning }) => {
  const [manualCode, setManualCode] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isScanning) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isScanning]);

  const startCamera = async () => {
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices?.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef?.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (error) {
      setCameraError('Camera access denied. Please enable camera permissions or use manual entry.');
      onToggleScanning();
    }
  };

  const stopCamera = () => {
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(track => track?.stop());
      streamRef.current = null;
    }
    if (videoRef?.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault();
    if (manualCode?.trim()) {
      onScanResult(manualCode?.trim());
      setManualCode('');
      setShowManualEntry(false);
    }
  };

  const simulateQRScan = () => {
    // Simulate QR code scan with mock credential data
    const mockQRData = JSON.stringify({
      type: 'vaccination_proof',
      credentialId: 'vc_vax_001',
      issuer: 'did:web:health.gov',
      subject: 'did:web:patient.example.com',
      issuanceDate: '2024-03-15T10:30:00Z',
      expirationDate: '2025-03-15T10:30:00Z',
      proof: {
        type: 'Ed25519Signature2020',
        created: '2024-03-15T10:30:00Z',
        verificationMethod: 'did:web:health.gov#key-1'
      }
    });
    onScanResult(mockQRData);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="QrCode" size={32} className="text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">QR Code Scanner</h2>
        <p className="text-muted-foreground">
          Scan a credential QR code or enter the code manually
        </p>
      </div>
      {/* Camera Scanner */}
      <div className="mb-6">
        {isScanning ? (
          <div className="relative">
            <div className="aspect-square max-w-md mx-auto bg-muted rounded-lg overflow-hidden">
              {cameraError ? (
                <div className="h-full flex items-center justify-center p-4">
                  <div className="text-center">
                    <Icon name="AlertCircle" size={48} className="text-warning mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">{cameraError}</p>
                  </div>
                </div>
              ) : (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
              )}
              
              {/* Scanning Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 border-2 border-primary rounded-lg relative">
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg"></div>
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-lg"></div>
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-lg"></div>
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg"></div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center space-x-4 mt-4">
              <Button variant="outline" onClick={onToggleScanning}>
                <Icon name="X" size={16} className="mr-2" />
                Stop Scanning
              </Button>
              <Button variant="default" onClick={simulateQRScan}>
                <Icon name="Scan" size={16} className="mr-2" />
                Simulate Scan
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="aspect-square max-w-md mx-auto bg-muted rounded-lg flex items-center justify-center mb-4">
              <div className="text-center">
                <Icon name="Camera" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Camera is ready to scan</p>
              </div>
            </div>
            
            <div className="flex justify-center space-x-4">
              <Button variant="default" onClick={onToggleScanning}>
                <Icon name="Camera" size={16} className="mr-2" />
                Start Scanning
              </Button>
              <Button variant="outline" onClick={() => setShowManualEntry(!showManualEntry)}>
                <Icon name="Keyboard" size={16} className="mr-2" />
                Manual Entry
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Manual Entry */}
      {showManualEntry && (
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Manual Code Entry</h3>
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <Input
              label="Credential Code"
              type="text"
              placeholder="Enter or paste the credential code here"
              value={manualCode}
              onChange={(e) => setManualCode(e?.target?.value)}
              description="Paste the full credential code or QR code data"
            />
            <div className="flex space-x-3">
              <Button type="submit" disabled={!manualCode?.trim()}>
                <Icon name="Check" size={16} className="mr-2" />
                Verify Code
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowManualEntry(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default QRScanner;